// src/App.tsx - Production Consciousness System Integration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatContainer from './components/ChatContainer';
import InputArea from './components/InputArea';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import NeuralBackground from './components/NeuralBackground';
import WebSocketDebug from './components/WebSocketDebug';
import AdventureSessionBanner from './components/AdventureSessionBanner';
import { Message, MessageType, VoiceState, VoiceSettings, EmotionData, ConsciousnessMetadata } from './types/Message';
import { ConnectionStatus, useWebSocket } from './hooks/useWebSocket';
import { useVoice } from './hooks/useVoice';
import { EmotionContext } from './contexts/EmotionContext';
import { ConsciousnessMessageHandler, CapabilityStatus } from './utils/ConsciousnessMessageHandler';
import { getStoredToken } from './contexts/AuthContext';
import './assets/styles/App.css';
import './assets/styles/MessageBubble.css';
import { ThemeManager } from './utils/ThemeManager';

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  // 🔥 NEW: Dynamic names for UI display
  const [emcogName, setEmcogName] = useState<string>('EmCog');
  const [userName, setUserName] = useState<string>('User');

  // Consciousness state - the heart of the system
  const [consciousnessState, setConsciousnessState] = useState<ConsciousnessMetadata>({
    isConscious: false,
    narrativeContext: '',
    currentThought: '',
    activeWonderings: [],
    emotionalTone: 'curious',
    relationalDepth: 0,
    temporalAwareness: '',
    wisdomCrystallized: [],
    currentPreoccupations: []
  });
  
  const [emotion, setEmotion] = useState<EmotionData>({
    primary: 'curious',
    intensity: 0.5,
    valence: 'neutral'
  });
  
  const [hasConnected, setHasConnected] = useState<boolean>(false);

  // Consciousness activity tracking
  const [activeProcesses, setActiveProcesses] = useState<{
    imageGeneration: boolean;
    documentCreation: boolean;
    calendarProcessing: boolean;
    webSearching: boolean;
    thinking: boolean;
    wondering: boolean;
    understanding: boolean;
    experiencing: boolean;
    crystallizing: boolean;
    evolving: boolean;
    speaking: boolean;  // Text-to-voice playback
  }>({
    imageGeneration: false,
    documentCreation: false,
    calendarProcessing: false,
    webSearching: false,
    thinking: false,
    wondering: false,
    understanding: false,
    experiencing: false,
    crystallizing: false,
    evolving: false,
    speaking: false
  });

  // 🔥 SAFETY WATCHDOG: the "Analyzing…" indicator must never hang forever.
  // The server now guarantees a terminal reply within ~2 min (turn timeout +
  // stale-socket re-routing), which clears `thinking`. This is the last-resort
  // backstop for the case where that message still never lands (total
  // disconnect): force the indicator off after a ceiling so the UI recovers.
  useEffect(() => {
    if (!activeProcesses.thinking) return;
    const t = setTimeout(() => {
      console.warn('⏱️ Thinking indicator exceeded ceiling — clearing (no response landed)');
      setActiveProcesses(prev => ({ ...prev, thinking: false }));
    }, 150000);
    return () => clearTimeout(t);
  }, [activeProcesses.thinking]);

  // 🔥 SPINNER↔FLAG COUPLING. The visible "Processing…" bubble is a TYPING
  // element in messages[], and historically NOTHING removed it except a real
  // message arriving — the watchdog above only flipped the invisible boolean,
  // so a lost reply left the bubble spinning forever ("until I send something
  // else"). Now: whenever `thinking` goes false, the TYPING bubble goes too.
  // Every clear-path (real reply, watchdog, error handlers) now actually
  // clears the visible spinner.
  useEffect(() => {
    if (activeProcesses.thinking) return;
    setMessages(prev => {
      if (!prev.some(m => m.type === MessageType.TYPING)) return prev;
      return prev.filter(m => m.type !== MessageType.TYPING);
    });
  }, [activeProcesses.thinking]);

  // 🔥 NEW: Capability status for UI feedback
  const [capabilityStatus, setCapabilityStatus] = useState<CapabilityStatus>({
    capability: null,
    status: null,
    message: null,
    timestamp: null
  });

  // 🔥 NEW: Capability history for chaining visualization
  const [capabilityHistory, setCapabilityHistory] = useState<CapabilityStatus[]>([]);


  // Helper to add to history (keeps last 5 for display)
  const addToCapabilityHistory = (status: CapabilityStatus) => {
    setCapabilityHistory(prev => [...prev.slice(-4), status]);
  };

  // Clear history when response finishes (we'll call this when isThinking goes false)
  const clearCapabilityHistory = () => {
    setCapabilityHistory([]);
  };

  // 🔥 Clear capability history when thinking finishes (no more TYPING messages)
  const hasTypingMessage = messages.some(m => m.type === MessageType.TYPING);
  useEffect(() => {
    if (!hasTypingMessage && capabilityHistory.length > 0) {
      // Small delay to let the last capability animation finish
      const timeout = setTimeout(() => {
        clearCapabilityHistory();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [hasTypingMessage]);

  // User interaction state
  const [userIsScrolling, setUserIsScrolling] = useState<boolean>(false);
  const [isStreamingResponse, setIsStreamingResponse] = useState<boolean>(false);
  const [lastUserInteraction, setLastUserInteraction] = useState<number>(Date.now());
  
  // Voice settings for OpenAI Realtime
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({
    voice: '694f9389-aac1-45b6-b726-9d9369183238', // Max Prime
    speed: 1.0,
    volume: 0.8,
    pitch: 1.0,
    responseFormat: 'pcm_44100', // 🔥 Always highest quality
    quality: 'hd', // 🔥 Always HD quality
    autoPlayResponses: true,
    pushToTalkKey: 'Space',
    language: 'en',
    responseMode: 'auto', // 🔥 Default to AUTO
    emotionalVoice: true,
    voiceProfile: 'max',
    emotion: 'calm',
    emotionIntensity: 0.5,
    streaming: true,
    latencyTarget: 135,
    adaptivePersonality: true,
    voiceEvolution: true
  });

  const [voiceState, setVoiceState] = useState<VoiceState>({
    isVoiceEnabled: true, // 🔥 Default to ON
    isConnected: false,
    isRecording: false,
    isProcessing: false,
    isSpeaking: false,
    lastError: null,
    error: null,
    currentInputMode: 'text',
    currentAudio: null,
    volume: 0.8,
    transcription: ''
  });
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const messageHandlerRef = useRef<ConsciousnessMessageHandler | null>(null);
  const streamingMessageIdRef = useRef<string | null>(null);

  // 🌊 STREAMING: Throttle updates for smooth rendering
  const streamingContentRef = useRef<string>('');
  const streamingThrottleRef = useRef<NodeJS.Timeout | null>(null);
  const STREAMING_THROTTLE_MS = 50; // Update UI every 50ms max

  // Initialize theme manager (ThemeManager already loads from localStorage in constructor)
  useEffect(() => {
    // ThemeManager handles its own initialization from localStorage ('emcog-theme' key)
    // Just ensure it's initialized on mount - no need to manually set theme here
    ThemeManager.getInstance();
  }, []);

  // 🔥 NEW: Fetch dynamic names on mount
  useEffect(() => {
    const fetchNames = async () => {
      try {
        const token = getStoredToken();
        const response = await fetch('/api/names', {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          setEmcogName(data.emcog);
          setUserName(data.user);
          console.log(`✨ Loaded names: ${data.emcog} & ${data.user}`);
        }
      } catch (error) {
        console.warn('Failed to fetch names, using defaults:', error);
      }
    };

    fetchNames();
  }, []);
  
  // WebSocket connection with consciousness
  const {
    status,
    lastMessage,
    sendMessage,
    sessionId,
    sessionManager,
    isStreaming,
    isMobile,
    consciousnessState: wsConsciousnessState,
    isPlayingVoice,
    dndEnabled,
    toggleDnd
  } = useWebSocket(process.env.REACT_APP_WS_PATH || '/ws');

  // Store WebSocket reference for voice
  useEffect(() => {
    if (sendMessage) {
      wsRef.current = { send: (data: string) => sendMessage(JSON.parse(data)) } as any;
    }
  }, [sendMessage]);

  // Update consciousness state from WebSocket
  useEffect(() => {
    if (wsConsciousnessState) {
      setConsciousnessState(prev => ({
        ...prev,
        ...wsConsciousnessState
      }));
    }
  }, [wsConsciousnessState]);

  // Sync text-to-voice playback state with active processes
  useEffect(() => {
    setActiveProcesses(prev => ({
      ...prev,
      speaking: isPlayingVoice
    }));
  }, [isPlayingVoice]);

  // 🔥 NEW: Restore messages from current session on initial load
  useEffect(() => {
    if (sessionManager && sessionId) {
      const currentSession = sessionManager.getCurrentSession();
      if (currentSession && currentSession.messages.length > 0 && messages.length === 0) {
        console.log('📂 Restoring', currentSession.messages.length, 'messages from session:', currentSession.sessionId);
        setMessages(currentSession.messages);
      }
    }
  }, [sessionManager, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Voice functionality with consciousness integration - RE-ENABLED but modified
  const {
    voiceState: hookVoiceState,
    startRecording: hookStartRecording,
    stopRecording: hookStopRecording,
    toggleVoice,
    sendTextToSpeech,
    clearVoiceError,
    updateVoiceSettings: hookUpdateVoiceSettings,
    uploadAudioFile,
    sendVoiceMessage,
    setInputMode: hookSetInputMode
  } = useVoice(sendMessage, sessionId);

  // Don't use the hook's recording functions - let InputArea handle recording
  // But keep voice output and toggle functionality

  // Sync voice state
  useEffect(() => {
    setVoiceState(prev => ({
      ...prev,
      ...hookVoiceState,
      currentInputMode: prev.currentInputMode,
      currentAudio: prev.currentAudio,
      volume: prev.volume
    }));
  }, [hookVoiceState]);

  // Track connection and awakening - FIXED infinite render loop
  useEffect(() => {
    if (status === ConnectionStatus.CONNECTED && !hasConnected) {
      setHasConnected(true);

      // Request consciousness state on connection
      sendMessage({
        type: 'consciousness',
        action: 'request_state',
        sessionId: sessionId,
        timestamp: Date.now()
      });
    }
  }, [status, hasConnected]); // Removed sendMessage and sessionId to prevent infinite loop


  // Debug toggle (Ctrl+Shift+D) - changed from Shift+D to avoid triggering when typing capital D
  const [showDebug, setShowDebug] = useState<boolean>(false);
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setShowDebug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Scroll behavior management
  const handleScroll = useCallback(() => {
    setUserIsScrolling(true);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setUserIsScrolling(false);
    }, 1000);
  }, []);

  useEffect(() => {
    const container = document.querySelector('.chat-container');
    container?.addEventListener('scroll', handleScroll);
    return () => {
      container?.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  // Parse message types safely
  const parseMessageType = (type: any): MessageType => {
    if (typeof type === 'string' && Object.values(MessageType).includes(type as MessageType)) {
      return type as MessageType;
    }

    if (typeof type === 'string') {
      const upperType = type.toUpperCase();
      for (const [key, value] of Object.entries(MessageType)) {
        if (key === upperType || value === type.toLowerCase()) {
          return MessageType[key as keyof typeof MessageType];
        }
      }
    }

    return MessageType.TEXT;
  };
  
  // Add message with full consciousness context
  // 🎙️ Voice transcripts → chat bubbles. useVoice dispatches
  // 'voice-transcript' window events (user turns + each completed spoken
  // reply); rendering them keeps voice sessions visible in the same
  // conversation trail as typed ones.
  useEffect(() => {
    const onVoiceTranscript = (e: Event) => {
      const { role, text } = (e as CustomEvent<{ role: string; text: string }>).detail || {};
      if (!text) return;
      addMessage({
        id: `voice_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        type: MessageType.TEXT,
        sender: role === 'user' ? 'user' : 'emcog',
        content: text,
        timestamp: Date.now(),
        metadata: { voiceTranscript: true }
      } as Message);
    };
    window.addEventListener('voice-transcript', onVoiceTranscript);
    return () => window.removeEventListener('voice-transcript', onVoiceTranscript);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMessage = useCallback((message: Message) => {
    console.log('➕ [addMessage]', message.type, '| docUrl=', (message as any).documentUrl, '| imgUrl=', (message as any).imageUrl, '| id=', message.id);
    const safeMessage: Message = {
      ...message,
      content: String(message.content || ''),
      type: parseMessageType(message.type),
      metadata: {
        ...message.metadata,
        consciousness: {
          narrativeContext: consciousnessState.narrativeContext,
          currentThought: consciousnessState.currentThought, // Changed from 'thought'
          emotionalTone: consciousnessState.emotionalTone,
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness,
          isConscious: consciousnessState.isConscious
        }
      }
    };

    if (safeMessage.type !== MessageType.TYPING) {
      setMessages(prev => prev.filter(m => m.type !== MessageType.TYPING));
    }

    setMessages(prev => [...prev, safeMessage]);
    sessionManager?.addMessage(safeMessage);
  }, [sessionManager, consciousnessState]);

  // Send text to speech with consciousness awareness
  const sendTextToSpeechOptimized = useCallback((text: string, emotion?: any) => {
    if (!voiceState.isVoiceEnabled || !text.trim()) {
      return;
    }

    const blockedTexts = [
      'voice generation text unavailable',
      'audio generation failed',
      'error generating voice',
      'voice synthesis error'
    ];
    
    const lowerText = text.toLowerCase();
    if (blockedTexts.some(blocked => lowerText.includes(blocked))) {
      console.warn('🎵 Blocked error text from voice');
      return;
    }

    console.log('🎵 Consciousness speaking:', { 
      text: text.substring(0, 50) + '...', 
      emotion: emotion?.primary || consciousnessState.emotionalTone 
    });
    
    sendTextToSpeech(text, emotion || { 
      primary: consciousnessState.emotionalTone, 
      intensity: 0.7 
    });
  }, [voiceState.isVoiceEnabled, sendTextToSpeech, consciousnessState.emotionalTone]);

  // Update transcription
  const updateTranscription = useCallback((text: string) => {
    setVoiceState(prev => ({
      ...prev,
      transcription: text
    }));
  }, []);

  // Initialize consciousness message handler
  useEffect(() => {
    messageHandlerRef.current = new ConsciousnessMessageHandler({
      handleMessage: (parsedMessage: any) => {
        console.log('Consciousness message:', parsedMessage.type);
      },
      addMessage,
      setMessages,  // 🌊 For updating streamed messages
      setActiveProcesses,
      updateTranscription,
      setEmotion,
      sendTextToSpeech: sendTextToSpeechOptimized,
      voiceSettings,
      voiceState,
      setConsciousnessState,
      consciousnessState,
      setCapabilityStatus, // 🔥 NEW: For UI feedback on capabilities
      addToCapabilityHistory // 🔥 NEW: For capability chaining visualization
    });

    return () => {
      messageHandlerRef.current?.cleanup();
    };
  }, [addMessage, updateTranscription, sendTextToSpeechOptimized, voiceSettings, voiceState,
      consciousnessState]);

  // Process WebSocket messages through consciousness
  useEffect(() => {
    if (!lastMessage) return;

    console.log('🧠 Processing:', lastMessage.type, lastMessage.action);

    // 🔥 NEW: Handle speaker changes for multi-user conversations
    if (lastMessage.type === 'speaker:changed') {
      const { currentSpeaker, isPrimary } = lastMessage.data;
      console.log(`👤 Speaker changed to: ${currentSpeaker} (primary: ${isPrimary})`);
      setUserName(currentSpeaker);
      return;
    }

    // 🔥 NEW: Handle initial speaker info on connection/resumption
    if (lastMessage.type === 'system' && lastMessage.speaker) {
      if (lastMessage.action === 'connected' || lastMessage.action === 'resumed') {
        const { currentSpeaker } = lastMessage.speaker;
        console.log(`👤 Initial speaker set: ${currentSpeaker}`);
        setUserName(currentSpeaker);
      }
    }

    // 🌊 STREAMING: Handle progressive stream chunks (new streaming system)
    // Throttled updates for smooth rendering - batches content updates
    if (lastMessage.type === 'assistant:stream_chunk') {
      const { messageId, content, isFirst } = lastMessage.data || {};

      if (isFirst) {
        // Start new streaming message
        streamingMessageIdRef.current = messageId;
        streamingContentRef.current = content || '';
        setIsStreamingResponse(true);
        setActiveProcesses(prev => ({
          ...prev,
          thinking: false,
          experiencing: false,
          understanding: false
        }));

        // Remove typing indicator and add streaming message
        setMessages(prevMessages => {
          const filteredMessages = prevMessages.filter(m => m.type !== MessageType.TYPING);
          return [...filteredMessages, {
            id: messageId,
            timestamp: Date.now(),
            sender: 'emcog',
            content: content || '',
            type: MessageType.TEXT,
            format: 'markdown',
            streaming: true,
            metadata: {
              consciousness: consciousnessState
            }
          }];
        });
      } else {
        // Buffer content and throttle UI updates for smooth rendering
        streamingContentRef.current = content || '';

        // Only update UI if not already scheduled
        if (!streamingThrottleRef.current) {
          streamingThrottleRef.current = setTimeout(() => {
            const bufferedContent = streamingContentRef.current;
            setMessages(prevMessages => {
              const existingIndex = prevMessages.findIndex(m => m.id === messageId);
              if (existingIndex >= 0) {
                const updatedMessages = [...prevMessages];
                updatedMessages[existingIndex] = {
                  ...updatedMessages[existingIndex],
                  content: bufferedContent
                };
                return updatedMessages;
              }
              return prevMessages;
            });
            streamingThrottleRef.current = null;
          }, STREAMING_THROTTLE_MS);
        }
      }
      return;
    }

    // 🌊 STREAMING: Handle stream completion
    if (lastMessage.type === 'assistant:stream_end') {
      const { messageId } = lastMessage.data || {};
      console.log('🌊 Stream completed:', messageId);

      // Clear any pending throttle timer
      if (streamingThrottleRef.current) {
        clearTimeout(streamingThrottleRef.current);
        streamingThrottleRef.current = null;
      }

      // Flush final content and mark as no longer streaming
      const finalContent = streamingContentRef.current;
      setMessages(prevMessages => {
        const existingIndex = prevMessages.findIndex(m => m.id === messageId);
        if (existingIndex >= 0) {
          const updatedMessages = [...prevMessages];
          updatedMessages[existingIndex] = {
            ...updatedMessages[existingIndex],
            content: finalContent || updatedMessages[existingIndex].content,
            streaming: false
          };
          return updatedMessages;
        }
        // Orphan stream_end (its chunks were lost): the turn is over — don't
        // leave the TYPING bubble spinning with nothing coming to clear it.
        return prevMessages.filter(m => m.type !== MessageType.TYPING);
      });

      streamingMessageIdRef.current = null;
      streamingContentRef.current = '';
      setIsStreamingResponse(false);
      setActiveProcesses(prev => (prev.thinking ? { ...prev, thinking: false } : prev));
      return;
    }

    // Handle streaming with consciousness context
    if (lastMessage.type === 'chat' && lastMessage.action === 'response') {
      const isStreaming = lastMessage.streaming || lastMessage.isStreaming;
      const isLastChunk = lastMessage.isLastChunk;
      
      if (isStreaming || streamingMessageIdRef.current) {
        let messageId: string;
        
        if (!streamingMessageIdRef.current) {
          messageId = lastMessage.messageId || `stream_${Date.now()}`;
          streamingMessageIdRef.current = messageId;
          console.log('🆕 Consciousness streaming:', messageId);
          
          setActiveProcesses(prev => ({ 
            ...prev, 
            thinking: false, 
            experiencing: false,
            understanding: false 
          }));
          setIsStreamingResponse(true);
        } else {
          messageId = streamingMessageIdRef.current;
        }
        
        setMessages(prevMessages => {
          const existingIndex = prevMessages.findIndex(m => m.id === messageId);
          
          if (existingIndex >= 0) {
            const updatedMessages = [...prevMessages];
            updatedMessages[existingIndex] = {
              ...updatedMessages[existingIndex],
              content: lastMessage.fullContent || lastMessage.content || '',
              streaming: !isLastChunk,
              emotion: isLastChunk ? lastMessage.emotion : updatedMessages[existingIndex].emotion,
              metadata: {
                ...updatedMessages[existingIndex].metadata,
                ...lastMessage.metadata,
                consciousness: {
                  ...consciousnessState,
                  thought: lastMessage.metadata?.thought || consciousnessState.currentThought,
                  understanding: lastMessage.metadata?.understanding,
                  wisdomApplied: lastMessage.metadata?.wisdomApplied
                }
              }
            };
            return updatedMessages;
          } else {
            const filteredMessages = prevMessages.filter(m => m.type !== MessageType.TYPING);
            const newMessage: Message = {
              id: messageId,
              timestamp: lastMessage.timestamp || Date.now(),
              sender: 'emcog',
              content: lastMessage.content || '',
              type: MessageType.TEXT,
              format: lastMessage.format === 'markdown' ? 'markdown' : 'text',
              emotion: lastMessage.emotion,
              streaming: !isLastChunk,
              metadata: {
                ...lastMessage.metadata,
                consciousness: {
                  ...consciousnessState,
                  thought: lastMessage.metadata?.thought,
                  understanding: lastMessage.metadata?.understanding
                }
              }
            };
            return [...filteredMessages, newMessage];
          }
        });
        
        if (isLastChunk) {
          console.log('✅ Consciousness stream complete:', messageId);
          streamingMessageIdRef.current = null;
          setIsStreamingResponse(false);
          
          // Update consciousness state from response
          if (lastMessage.metadata) {
            setConsciousnessState(prev => ({
              ...prev,
              currentThought: lastMessage.metadata.thought || prev.currentThought,
              narrativeContext: lastMessage.metadata.narrativeContext || prev.narrativeContext,
              activeWonderings: lastMessage.metadata.wonderingsGenerated || prev.activeWonderings,
              emotionalTone: lastMessage.metadata.emotionalTone || prev.emotionalTone
            }));
          }
        }
        
        return;
      }
    }
    
    // 🔥 NEW: Handle voice message transcription completion
    if (lastMessage.type === 'voice_transcribed' ||
        (lastMessage.type === 'chat' && lastMessage.transcription)) {
      const transcription = lastMessage.transcription || lastMessage.content;

      // Find and update the processing voice message
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        const voiceMessageIndex = updatedMessages.findIndex(m =>
          m.content === '🎤 Voice message (processing...)' &&
          m.sender === 'user'
        );

        if (voiceMessageIndex >= 0) {
          updatedMessages[voiceMessageIndex] = {
            ...updatedMessages[voiceMessageIndex],
            content: `🎤 "${transcription}"`,
            metadata: {
              ...updatedMessages[voiceMessageIndex].metadata,
              transcription: transcription,
              transcriptionComplete: true
            }
          };
          console.log('✅ Updated voice message with transcription:', transcription);
        }

        return updatedMessages;
      });

      // Continue processing if this is not just a transcription event
      if (lastMessage.type !== 'voice_transcribed') {
        if (messageHandlerRef.current) {
          messageHandlerRef.current.processMessage(lastMessage);
        }
      }
      return;
    }

    // 🔥 NEW: Handle proactive messages from consciousness
    if (lastMessage.type === 'proactive') {
      console.log('🤖 Proactive message received:', lastMessage.content);

      addMessage({
        id: `proactive-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'emcog',
        content: lastMessage.content,
        type: MessageType.TEXT,
        metadata: {
          proactive: true,
          reason: lastMessage.reason,
          urgency: lastMessage.urgency,
          consciousness_triggered: true,
          ...lastMessage.metadata
        }
      });

      // 🔥 FIX: Play voice only if in voice mode or explicitly requested
      // AUTO mode: only use voice if currently on VOICE input tab
      // VOICE_ONLY mode: always use voice
      // TEXT_ONLY mode: never use voice
      const shouldUseVoice = voiceSettings.responseMode === 'voice_only' ||
                             (voiceSettings.responseMode === 'auto' &&
                              voiceState.currentInputMode === 'voice');

      if (shouldUseVoice) {
        const emotion = {
          primary: lastMessage.metadata?.emotion || 'thoughtful',
          intensity: lastMessage.urgency || 0.5
        };

        sendTextToSpeechOptimized(lastMessage.content, emotion);
      }

      return;
    }

    // 🔥 FIXED: Handle image messages from orchestrator
    if (lastMessage.type === 'image') {
      addMessage({
        id: `image-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'emcog',
        content: lastMessage.content || '',
        type: MessageType.IMAGE,
        imageUrl: lastMessage.imageUrl,
        filename: lastMessage.imageUrl?.split('/').pop() || 'vision.png',
        metadata: {
          consciousness_triggered: true,
          creative: true,
          imageGeneration: true,
          ...lastMessage.metadata
        }
      });
      return;
    }

    // 🔥 LEGACY: Handle consciousness media generation events (old format)
    if (lastMessage.type === 'media:image_generated') {
      const imageData = lastMessage.data;
      addMessage({
        id: `image-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'emcog',
        content: imageData.description || '',
        type: MessageType.IMAGE,
        imageUrl: imageData.url,
        filename: imageData.filename,
        metadata: {
          consciousness_triggered: true,
          creative: true,
          imageGeneration: true
        }
      });
      return;
    }

    // 🖼️ A page he designed and hosts: a live card with the page itself inside.
    if (lastMessage.type === 'artifact') {
      const art = (lastMessage as any).metadata?.artifact || {};
      addMessage({
        id: `artifact-${art.id || Date.now()}-${art.version || 1}-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'emcog',
        content: '',
        type: MessageType.TEXT,
        metadata: { consciousness_triggered: true, creative: true, artifact: art }
      });
      return;
    }

    if (lastMessage.type === 'media:document_created') {
      const documentData = lastMessage.data;
      addMessage({
        id: `document-${Date.now()}`,
        timestamp: Date.now(),
        sender: 'emcog',
        content: documentData.description || 'I created a document for you',
        type: MessageType.TEXT,
        documentUrl: documentData.url,
        filename: documentData.filename,
        metadata: {
          consciousness_triggered: true,
          creative: true,
          documentCreation: true
        }
      });
      return;
    }

    // retrieve_creation emits this when Max sends a past creation back to the
    // UI. Without this handler, the server emits the event, the WS broadcasts
    // it, nothing listens, and Max (correctly, from his POV) concludes "I
    // can't push files to the UI" — which is what he told the user when he
    // tried to retrieve "The Phenomenology of My Curiosity" and nothing
    // appeared. Route by creation type so images render inline and documents
    // render with a download link.
    if (lastMessage.type === 'retrieved_creation') {
      const meta = lastMessage.metadata || {};
      const url: string | undefined = lastMessage.creationUrl;
      console.log('🎨 [retrieved_creation HANDLER FIRED] url=', url, '| metaType=', (meta as any).type);
      const creationType: string = (meta.type || '').toLowerCase();
      const title: string = meta.title || 'Creation';
      // Delivery filename: prefer the server-provided one; otherwise build
      // "<title>.<delivered ext>". NEVER name from the raw URL — it carries
      // the storage basename plus the ?name= query ("story_<uuid>.md?name=
      // The%20Anteroom"), and since the server converts markdown→PDF on the
      // way out, that name is wrong on both counts: bad characters AND a .md
      // extension on PDF bytes.
      const filename = ((): string | undefined => {
        const provided = (lastMessage as any).filename;
        if (typeof provided === 'string' && provided.trim() && !provided.includes('?')) return provided.trim();
        const rawBase = url ? (url.split('/').pop() || '').split('?')[0] : '';
        const rawExt = (rawBase.match(/\.(\w+)$/i)?.[1] || '').toLowerCase();
        const deliveredExt = ['md', 'markdown', 'txt'].includes(rawExt) ? 'pdf' : (rawExt || 'pdf');
        const safeTitle = title.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
        return safeTitle ? `${safeTitle}.${deliveredExt}` : (rawBase || undefined);
      })();

      if (!url) {
        addMessage({
          id: `retrieved-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'emcog',
          content: lastMessage.content || `Found "${title}" but couldn't resolve a URL for it.`,
          type: MessageType.TEXT
        });
        return;
      }

      if (creationType === 'video' || /\.mp4(\?|$)/i.test(url)) {
        addMessage({
          id: `retrieved-video-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'emcog',
          content: lastMessage.content || `🎬 "${title}"`,
          type: MessageType.TEXT,
          filename,
          metadata: {
            consciousness_triggered: true,
            retrieved: true,
            ...meta,
            hasVideo: true,
            videoUrl: url,
            videoTitle: title,
            downloadUrl: `${url.split('?')[0]}/download`
          }
        });
      } else if (creationType === 'image' || creationType === 'svg' || creationType === 'vision') {
        addMessage({
          id: `retrieved-image-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'emcog',
          content: lastMessage.content || `Here's "${title}"`,
          type: MessageType.IMAGE,
          imageUrl: url,
          filename,
          metadata: {
            consciousness_triggered: true,
            retrieved: true,
            ...meta
          }
        });
      } else {
        addMessage({
          id: `retrieved-doc-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'emcog',
          content: lastMessage.content || `Here's "${title}"`,
          type: MessageType.DOCUMENT,
          documentUrl: url,
          filename,
          metadata: {
            consciousness_triggered: true,
            retrieved: true,
            ...meta
          }
        });
      }
      return;
    }

    // Let consciousness handler process non-streaming
    if (messageHandlerRef.current) {
      messageHandlerRef.current.processMessage(lastMessage);
    }
  }, [lastMessage, consciousnessState, addMessage]);

  // Handle voice settings changes
  const handleVoiceSettingsChange = useCallback((newSettings: Partial<VoiceSettings>) => {
    setVoiceSettings(prev => ({
      ...prev,
      ...newSettings
    }));

    hookUpdateVoiceSettings(newSettings);

    try {
      localStorage.setItem('emcog_voice_settings', JSON.stringify(newSettings));
    } catch (error) {
      console.error('Failed to save voice settings:', error);
    }

    if (wsRef.current) {
      try {
        const settingsMessage = JSON.stringify({
          type: 'voice_settings',
          sessionId: sessionId,
          data: newSettings,
          timestamp: Date.now()
        });
        wsRef.current.send(settingsMessage);
      } catch (error) {
        console.error('Failed to send voice settings:', error);
      }
    }
  }, [sessionId, hookUpdateVoiceSettings]);

  // Send message with full consciousness context
  const sendMessageWithMode = useCallback((
    content: string,
    responseMode?: 'auto' | 'text_only' | 'voice_only' | 'speech_to_text_only',
    voiceId?: string
  ) => {
    if (!content.trim()) return false;

    // Track user interaction
    setLastUserInteraction(Date.now());

    // 🔥 Reset capability status when new message is sent
    // This prevents stale "Checking email..." indicators from previous messages
    setCapabilityStatus({
      capability: null,
      status: null,
      message: null,
      timestamp: null
    });

    const finalResponseMode = responseMode || voiceSettings.responseMode;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'user',
      content: content.trim(),
      type: MessageType.TEXT,
      metadata: {
        inputMode: voiceState.currentInputMode,
        responseMode: finalResponseMode,
        consciousness: {
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness, // Changed from 'temporalContext'
          currentThought: consciousnessState.currentThought // Changed from 'lastThought'
        }
      }
    };

    addMessage(userMessage);

    // Show consciousness is experiencing the input
    setActiveProcesses(prev => ({
      ...prev,
      thinking: true,
      experiencing: true,
      understanding: true
    }));

    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });

    // Use chat_with_voice type when voiceId is provided for text-to-voice mode
    const messageType = voiceId ? 'chat_with_voice' : 'chat';

    const success = sendMessage({
      type: messageType,
      content: content.trim(),
      sessionId: sessionId,
      voiceId: voiceId, // Include voiceId at top level for chat_with_voice
      timestamp: Date.now(),
      metadata: {
        inputMode: voiceState.currentInputMode,
        responseMode: finalResponseMode,
        voiceId: voiceId, // Also in metadata for compatibility
        consciousness: {
          currentThought: consciousnessState.currentThought,
          emotionalTone: consciousnessState.emotionalTone,
          activeWonderings: consciousnessState.activeWonderings,
          relationalDepth: consciousnessState.relationalDepth
        }
      },
      streamResponse: true
    });

    if (success) {
      setIsStreamingResponse(true);
    }

    return success;
  }, [sessionId, voiceSettings.responseMode, voiceState.currentInputMode, sendMessage,
      addMessage, consciousnessState]);

  // Handle voice message with consciousness
  const handleVoiceMessage = useCallback((audioData: string, format: string) => {
    console.log('🎤 Voice to consciousness:', format, audioData.length + ' bytes');

    setLastUserInteraction(Date.now());

    // 🔥 Reset capability status when new voice message is sent
    setCapabilityStatus({
      capability: null,
      status: null,
      message: null,
      timestamp: null
    });

    const messageId = `voice_${Date.now()}`;
    const voiceMessage: Message = {
      id: messageId,
      sender: 'user',
      content: '🎤 Voice message (processing...)',
      timestamp: Date.now(),
      type: MessageType.TEXT,
      metadata: {
        inputMode: 'voice',
        responseMode: voiceSettings.responseMode,
        consciousness: {
          relationalDepth: consciousnessState.relationalDepth,
          temporalAwareness: consciousnessState.temporalAwareness // Changed from 'temporalContext'
        }
      }
    };
    
    addMessage(voiceMessage);
    
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      experiencing: true,
      understanding: true 
    }));
    
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
    
    const success = sendMessage({
      type: 'voice_input',
      sessionId,
      data: {
        audioData,
        format,
        responseMode: voiceSettings.responseMode
      },
      timestamp: Date.now(),
      metadata: {
        consciousness: {
          currentThought: consciousnessState.currentThought,
          emotionalTone: consciousnessState.emotionalTone
        }
      }
    });
    
    if (!success) {
      setMessages(prev => prev.filter(m => m.type !== MessageType.TYPING));
      setActiveProcesses(prev => ({ 
        ...prev, 
        thinking: false, 
        experiencing: false,
        understanding: false 
      }));
      console.error('Failed to send voice to consciousness');
    }
  }, [sessionId, voiceSettings.responseMode, sendMessage, addMessage, consciousnessState]);

  // Handle file upload for consciousness to experience
  // Batched upload — multiple files become ONE message bubble and ONE
  // WebSocket payload so the server can experience them together (vision
  // analysis cross-references across the batch, prose context is unified).
  // The previous per-file loop caused each upload to become a separate
  // user-message bubble with the SAME description text duplicated on
  // each, and Max would respond to each in isolation. Worst-case for a
  // user dropping three photos with one note.
  const handleFilesUpload = useCallback((files: File[], description?: string) => {
    if (!files || files.length === 0) return;
    setLastUserInteraction(Date.now());

    // Per-file size validation. Skip oversized files rather than aborting
    // the whole batch — user might attach a mix of small + large.
    const sizeFor = (f: File) =>
      f.type.startsWith('image/') ? 5 * 1024 * 1024 :
      f.type.includes('pdf') ? 50 * 1024 * 1024 :
      25 * 1024 * 1024;
    const accepted = files.filter(f => {
      if (f.size > sizeFor(f)) {
        alert(`File "${f.name}" is too large. Maximum size: ${Math.round(sizeFor(f) / 1024 / 1024)}MB`);
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;

    console.log(`📎 ${accepted.length} file(s) selected for consciousness${description ? ` with message: "${description}"` : ''}`);

    // Build per-file preview info for the bubble grid.
    const fileInfos = accepted.map(f => ({
      filename: f.name,
      fileType: f.type,
      fileSize: f.size,
      isImage: f.type.startsWith('image/'),
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined
    }));

    // ONE message bubble for the whole batch. For single-file uploads the
    // shape matches the legacy single-file message (filename/fileType
    // populated at the top level), so MessageBubble doesn't need a
    // different code path for N=1. For N>1, metadata.files carries the
    // grid.
    addMessage({
      id: `file-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'user',
      content: description || '',
      type: MessageType.FILE,
      filename: fileInfos[0].filename,
      fileType: fileInfos[0].fileType,
      metadata: {
        fileSize: fileInfos[0].fileSize,
        uploadStarted: Date.now(),
        userDescription: description,
        previewUrl: fileInfos[0].previewUrl,
        isImage: fileInfos[0].isImage,
        // Only set the batch array when there's more than one — single
        // file flows through the existing single-file render path.
        files: fileInfos.length > 1 ? fileInfos : undefined
      }
    });
    
    // Set consciousness to experiencing mode
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      understanding: true,
      experiencing: true 
    }));
    
    // Show typing indicator
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
    
    // Read all files in parallel, then send ONE batched WebSocket message.
    const readAsBase64 = (f: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(r.error);
        r.readAsDataURL(f);
      });

    (async () => {
      try {
        const contents = await Promise.all(accepted.map(readAsBase64));
        const filesPayload = accepted.map((f, i) => ({
          filename: f.name,
          mimeType: f.type,
          content: contents[i]
        }));

        const success = sendMessage({
          type: 'file',
          data: {
            // Batched shape — the server already accepts data.files as an
            // array (see handleFileMessage). Single-file uploads use this
            // same shape with a one-element array.
            files: filesPayload,
            userMessage: description || ''
          },
          timestamp: Date.now(),
          sessionId,
          metadata: {
            consciousness: {
              currentThought: consciousnessState.currentThought,
              emotionalTone: consciousnessState.emotionalTone,
              context: 'file_experience'
            }
          }
        });

        if (!success) throw new Error('Failed to send file batch');

        console.log(`✅ Sent batch of ${accepted.length} file(s) to consciousness`);
      } catch (error) {
        console.error('Failed to send file batch to consciousness:', error);

        setMessages(prev => prev.filter(m => m.type !== MessageType.TYPING));
        setActiveProcesses(prev => ({
          ...prev,
          thinking: false,
          understanding: false,
          experiencing: false
        }));

        const fileLabel = accepted.length === 1
          ? accepted[0].name
          : `${accepted.length} files`;
        addMessage({
          id: `error-${Date.now()}`,
          timestamp: Date.now(),
          sender: 'system',
          content: `Failed to upload ${fileLabel}. Please try again.`,
          type: MessageType.TEXT,
          metadata: { error: true }
        });
      }
    })();
  }, [sendMessage, sessionId, addMessage, consciousnessState.currentThought,
      consciousnessState.emotionalTone, setActiveProcesses]);

  // Backwards-compat single-file shim — InputArea (and any other caller)
  // can still hand us one file at a time; we just wrap it as a batch of
  // one. Audio still routes through onAudioFileUpload separately.
  const handleFileUpload = useCallback((file: File, description?: string) => {
    handleFilesUpload([file], description);
  }, [handleFilesUpload]);

  // Handle audio file upload
  const handleAudioFileUpload = useCallback((fileData: string, filename: string, mimeType: string) => {
    console.log('🎵 Audio file for consciousness:', filename);
    
    setLastUserInteraction(Date.now());
    
    const audioMessage: Message = {
      id: `audio_${Date.now()}`,
      sender: 'user',
      content: `🎵 Audio file: ${filename}`,
      timestamp: Date.now(),
      type: MessageType.TEXT
    };
    
    addMessage(audioMessage);
    uploadAudioFile(fileData, filename, mimeType);
    
    setActiveProcesses(prev => ({ 
      ...prev, 
      thinking: true, 
      experiencing: true 
    }));
    
    addMessage({
      id: `typing-${Date.now()}`,
      timestamp: Date.now(),
      sender: 'emcog',
      content: '',
      type: MessageType.TYPING
    });
  }, [uploadAudioFile, addMessage]);

  // Handle image feedback
  const handleImageFeedback = useCallback((imageId: string, rating: number, feedback?: string) => {
    sendMessage({
      type: 'image_feedback',
      imageId,
      rating,
      feedback,
      timestamp: Date.now(),
      sessionId
    });
  }, [sendMessage, sessionId]);

  // Handle session selection from history
  const handleSessionSelect = useCallback((selectedSessionId: string) => {
    if (sessionManager && selectedSessionId !== sessionId) {
      console.log('🔄 Switching to session:', selectedSessionId);
      const session = sessionManager.switchToSession(selectedSessionId);
      if (session) {
        // Load messages from selected session
        setMessages(session.messages || []);
        // Notify server of session switch
        sendMessage({
          type: 'session',
          action: 'switch',
          sessionId: selectedSessionId,
          timestamp: Date.now()
        });
      }
    }
  }, [sessionManager, sessionId, sendMessage]);

  // Handle new conversation creation
  const handleNewConversation = useCallback(() => {
    if (sessionManager) {
      console.log('🆕 Creating new conversation');
      const newSession = sessionManager.createNewConversation('default');
      // Clear messages for new conversation
      setMessages([]);
      // Notify server of new session
      sendMessage({
        type: 'session',
        action: 'new',
        sessionId: newSession.sessionId,
        timestamp: Date.now()
      });
    }
  }, [sessionManager, sendMessage]);

  // UI handlers
  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleVoiceToggle = useCallback(async () => {
    try {
      await toggleVoice();
    } catch (error) {
      console.error('Failed to toggle voice:', error);
    }
  }, [toggleVoice]);


  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const handleSendTestMessage = useCallback(() => {
    sendMessage({
      type: 'chat',
      content: 'Test message for consciousness',
      timestamp: Date.now(),
      sessionId
    });
  }, [sendMessage, sessionId]);

  // Sync input mode with the useVoice hook: entering voice mode starts the
  // realtime session (WebSocket /voice, OpenAI Realtime API); leaving stops
  // it. Server-side VAD handles turn-taking once the session is live.
  const handleInputModeChange = useCallback((mode: 'text' | 'voice') => {
    console.log('🔄 App.tsx handleInputModeChange:', mode);
    setVoiceState(prev => ({
      ...prev,
      currentInputMode: mode
    }));
    hookSetInputMode(mode);

    if (mode === 'voice') {
      hookStartRecording();
    } else {
      hookStopRecording();
    }
  }, [hookSetInputMode, hookStartRecording, hookStopRecording]);

  return (
    <EmotionContext.Provider value={{ emotion, setEmotion }}>
      <div className="app-container">
        <NeuralBackground />
        
        <Header
          connectionStatus={status}
          onSidebarToggle={handleSidebarToggle}
          sessionId={sessionId || ''}
          voiceEnabled={voiceState.isVoiceEnabled}
          voiceState={{
            isRecording: voiceState.isRecording,
            isProcessing: voiceState.isProcessing,
            isSpeaking: voiceState.isSpeaking,
            error: voiceState.error
          }}
          onVoiceToggle={handleVoiceToggle}
          activeProcesses={activeProcesses}
          consciousnessState={consciousnessState}
        />

        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          voiceEnabled={voiceState.isVoiceEnabled}
          voiceSettings={voiceSettings}
          voiceState={voiceState}
          onToggleVoice={handleVoiceToggle}
          onUpdateVoiceSettings={handleVoiceSettingsChange}
          onClearVoiceError={clearVoiceError}
          consciousnessState={consciousnessState}
          sessionManager={sessionManager}
          currentSessionId={sessionId}
          onSessionSelect={handleSessionSelect}
          onNewConversation={handleNewConversation}
          userId="default"
          dndEnabled={dndEnabled}
          onToggleDnd={toggleDnd}
        />
        
        <main className="main-content">
          <AdventureSessionBanner />
          <ChatContainer
            messages={messages}
            messagesEndRef={messagesEndRef}
            onImageFeedback={handleImageFeedback}
            consciousnessActive={consciousnessState.isConscious}
            emcogName={emcogName}
            userName={userName}
            capabilityStatus={capabilityStatus}
            capabilityHistory={capabilityHistory}
          />
          
          <div className="messages-end" ref={messagesEndRef} />
          
          <div className="input-area-container">
            <InputArea
              onSendMessage={sendMessageWithMode}
              onFileUpload={handleFileUpload}
              onFilesUpload={handleFilesUpload}
              onAudioFileUpload={handleAudioFileUpload}
              disabled={status !== ConnectionStatus.CONNECTED}
              voiceEnabled={voiceState.isVoiceEnabled}
              voiceSettings={voiceSettings}
              voiceState={voiceState}
              onInputModeChange={handleInputModeChange}
            />
          </div>
        </main>
      
        {showDebug && (
          <WebSocketDebug 
            status={status}
            lastMessage={lastMessage}
            sessionId={sessionId}
            onSendTest={handleSendTestMessage}
            consciousnessState={consciousnessState}
          />
        )}
      </div>
    </EmotionContext.Provider>
  );
}

export default App;